<template>
  <span
    :class="[
      'svg-container',
      {
        [`${props.class}`]: !!props.class,
        'dark-color': isDark,
        'primary-color': isPrimary,
        circle: isCircle,
        filled: isFilled,
        padded: isPadded,
        error: isError,
        flag: isFlag,
      },
    ]"
    :style="{ fontSize: fontSizeValue, width: fontSizeValue, height: fontSizeValue, minWidth: fontSizeValue }"
    @click="emitIconEvent($event)"
  >
    <inline-svg
      v-if="getSource"
      :id="uuid"
      :src="getSource"
      :alt="`${props.name}`"
      @loaded="setIconSize($event)"
    ></inline-svg>
  </span>
</template>

<script lang="ts" setup>
import GamHelper from '@/core/helpers/app.helper';
import {
  GamIconName,
  GamIconSize,
  GamIconSpecialName,
  GamIconSpecialSize,
} from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import { computed, onMounted, onUpdated, ref, watch } from 'vue';
import InlineSvg from 'vue-inline-svg';

const props = withDefaults(defineProps<GamIconType>(), {
  size: GamIconSize.NORMAL,
});

const emit = defineEmits([GamComponentsEmits.CLICK]);

const id = props.id || 'icon';
const uuid = ref<string>(GamHelper.createId(id));
const svgIcon = ref<SVGElement>();
const fontSizeValue = ref<string>('');

onMounted(() => {
  setIconProperties();
});

onUpdated(() => {
  setIconProperties();
});

const setIconProperties = (): void => {
  fontSizeValue.value = setFontSize();
};

const setFontSize = (): GamIconSize | GamIconSpecialSize | string => {
  return props.size || GamIconSize.NORMAL;
};

const getSource = computed((): string => {
  return gamIconUrls[props.name];
});

const setIconSize = (svgElement: SVGElement): void => {
  svgIcon.value = svgElement;
  const svgElementViewBox = svgIcon.value?.getAttribute?.('viewBox');
  if (!svgElementViewBox) {
    return;
  }
  const viewBox: number[] | null = svgElementViewBox.split(' ').map((n) => Number(n));
  if (viewBox?.length) {
    const widthToHeight = (viewBox[2] / viewBox[3]).toFixed(2);
    svgIcon.value.setAttribute('height', '1em');
    svgIcon.value.setAttribute('width', `${widthToHeight}em`);
    svgIcon.value.classList.add('svg-class');
  }
  setGradient();
};

const setGradient = (): void => {
  if (props.isCurrent) {
    svgIcon.value?.children[0].setAttribute('fill', 'currentColor');
  } else if (props.isPrimary && !props.isError) {
    svgIcon.value?.children[0].setAttribute('fill', 'var(--color-pink)');
  } else if (props.isDark) {
    svgIcon.value?.children[0]?.setAttribute('fill', '#181716');
  } else if (props.isError) {
    svgIcon.value?.children[0]?.setAttribute('fill', '#ff0000');
  } else if (props.isDisabled) {
    svgIcon.value?.children[0]?.setAttribute('fill', '#666');
  } else {
    svgIcon.value?.children[0]?.setAttribute('fill', '#ffffff');
  }
};

const emitIconEvent = (event: Event): void => {
  emit(GamComponentsEmits.CLICK, event);
};

watch(
  () => props,
  () => {
    setFontSize();
    setGradient();
  },
  { deep: true },
);

const UNKNOWN = new URL('../../ui/images/logo-mark.svg', import.meta.url).href;
// Name
const MAP_2D = new URL('../../ui/icons/2d-map.svg', import.meta.url).href;
const MAP_3D = new URL('../../ui/icons/3d-map.svg', import.meta.url).href;
const ADMIN = new URL('../../ui/icons/admin.svg', import.meta.url).href;
const APPLE = new URL('../../ui/icons/apple.svg', import.meta.url).href;
const APPLEPAY = new URL('../../ui/icons/applepay.svg', import.meta.url).href;
const ARROW_DOWN = new URL('../../ui/icons/arrow-down.svg', import.meta.url).href;
const ARROW_LEFT = new URL('../../ui/icons/arrow-left.svg', import.meta.url).href;
const ARROW_RIGHT = new URL('../../ui/icons/arrow-right.svg', import.meta.url).href;
const ARROW_RIGHT_END = new URL('../../ui/icons/arrow-right-end.svg', import.meta.url).href;
const CHEVRON_LEFT = new URL('../../ui/icons/chevron-left.svg', import.meta.url).href;
const CHEVRON_RIGHT = new URL('../../ui/icons/chevron-right.svg', import.meta.url).href;
const ACCCOUNT_SETTINGS = new URL('../../ui/icons/account-settings.svg', import.meta.url).href;
const ARROW_UP = new URL('../../ui/icons/arrow-up.svg', import.meta.url).href;
const BELL_EMPTY = new URL('../../ui/icons/bell-empty.svg', import.meta.url).href;
const BELL = new URL('../../ui/icons/bell.svg', import.meta.url).href;
const BIGMAC = new URL('../../ui/icons/bigmac.svg', import.meta.url).href;
const BIGMAC_EMPTY = new URL('../../ui/icons/bigmac-empty.svg', import.meta.url).href;
const BIRD = new URL('../../ui/icons/bird.svg', import.meta.url).href;
const BULLET_LIST = new URL('../../ui/icons/bullet-list.svg', import.meta.url).href;
const BULLET_LIST_EMPTY = new URL('../../ui/icons/bullet-list-empty.svg', import.meta.url).href;
const CAMERA = new URL('../../ui/icons/camera.svg', import.meta.url).href;
const CHECK = new URL('../../ui/icons/check.svg', import.meta.url).href;
const PAUSE = new URL('../../ui/icons/pause.svg', import.meta.url).href;
const PLAY = new URL('../../ui/icons/play.svg', import.meta.url).href;
const PLAY_EMPTY = new URL('../../ui/icons/play-empty.svg', import.meta.url).href;
const PLAY_OUTLINE = new URL('../../ui/icons/play-outline.svg', import.meta.url).href;
const PUZZLE = new URL('../../ui/icons/puzzle.svg', import.meta.url).href;
const PUZZLE_GRID = new URL('../../ui/icons/puzzle-grid.svg', import.meta.url).href;
const CIRCLES_GRID = new URL('../../ui/icons/circles-grid.svg', import.meta.url).href;
const CHESS_CLOCK = new URL('../../ui/icons/chess-clock.svg', import.meta.url).href;
const CHESS_COM = new URL('../../ui/icons/chess-com.svg', import.meta.url).href;
const CHESS_SET = new URL('../../ui/icons/chess-set.svg', import.meta.url).href;
const CHESS_TABLE = new URL('../../ui/icons/chess-table.svg', import.meta.url).href;
const CLUB = new URL('../../ui/icons/club.svg', import.meta.url).href;
const COFFEE = new URL('../../ui/icons/coffee.svg', import.meta.url).href;
const COMPASS = new URL('../../ui/icons/compass.svg', import.meta.url).href;
const CORNER = new URL('../../ui/icons/corner.svg', import.meta.url).href;
const CROWN = new URL('../../ui/icons/crown.svg', import.meta.url).href;
const DOTS_H = new URL('../../ui/icons/dots-h.svg', import.meta.url).href;
const DOTS_H_EMPTY = new URL('../../ui/icons/dots-h-empty.svg', import.meta.url).href;
const DOTS_V = new URL('../../ui/icons/dots-v.svg', import.meta.url).href;
const DOUBLE_CHECK = new URL('../../ui/icons/double-check.svg', import.meta.url).href;
const DOWN = new URL('../../ui/icons/down.svg', import.meta.url).href;
const DOUBLE_DOWN = new URL('../../ui/icons/double-down.svg', import.meta.url).href;
const EDIT = new URL('../../ui/icons/edit.svg', import.meta.url).href;
const ENVELOPE = new URL('../../ui/icons/envelope.svg', import.meta.url).href;
const EXPLORE = new URL('../../ui/icons/explore.svg', import.meta.url).href;
const EXPLORE_EMPTY = new URL('../../ui/icons/explore-empty.svg', import.meta.url).href;
const FAST = new URL('../../ui/icons/fast.svg', import.meta.url).href;
const FAST_EMPTY = new URL('../../ui/icons/fast-empty.svg', import.meta.url).href;
const FAST_PAUSE = new URL('../../ui/icons/fast-pause.svg', import.meta.url).href;
const FEED = new URL('../../ui/icons/feed.svg', import.meta.url).href;
const FEMALE = new URL('../../ui/icons/female.svg', import.meta.url).href;
const FIDE = new URL('../../ui/icons/fide.svg', import.meta.url).href;
const FILTER = new URL('../../ui/icons/filter.svg', import.meta.url).href;
const FILTER_LEGIBLE = new URL('../../ui/icons/filter-legible.svg', import.meta.url).href;
const FILTER_APPLIED = new URL('../../ui/icons/filter-applied.svg', import.meta.url).href;
const TILDE = new URL('../../ui/icons/tilde.svg', import.meta.url).href;
const GAMBIT = new URL('../../ui/icons/gambit.svg', import.meta.url).href;
const GAMBIT_EMPTY = new URL('../../ui/icons/gambit-empty.svg', import.meta.url).href;
const GAMBIT_HALF = new URL('../../ui/icons/gambit-half.svg', import.meta.url).href;
const GIANT_CHESS = new URL('../../ui/icons/giant-chess.svg', import.meta.url).href;
const GOOGLE = new URL('../../ui/icons/google.svg', import.meta.url).href;
const HAMBURGER = new URL('../../ui/icons/hamburger.svg', import.meta.url).href;
const HANDSHAKE = new URL('../../ui/icons/handshake.svg', import.meta.url).href;
const HANDSHAKE_EMPTY = new URL('../../ui/icons/handshake-empty.svg', import.meta.url).href;
const HANDSHAKE_HALF_LEFT = new URL('../../ui/icons/handshake-half-left.svg', import.meta.url).href;
const HANDSHAKE_HALF_RIGHT = new URL('../../ui/icons/handshake-half-right.svg', import.meta.url).href;
const HEART = new URL('../../ui/icons/heart.svg', import.meta.url).href;
const HEART_EMPTY = new URL('../../ui/icons/heart-empty.svg', import.meta.url).href;
const HOUSE = new URL('../../ui/icons/house.svg', import.meta.url).href;
const IMAGE = new URL('../../ui/icons/image.svg', import.meta.url).href;
const INSTAGRAM = new URL('../../ui/icons/ig.svg', import.meta.url).href;
const INVITE = new URL('../../ui/icons/invite.svg', import.meta.url).href;
const INVITE_EMPTY = new URL('../../ui/icons/invite-empty.svg', import.meta.url).href;
const INVITE_PLAYER = new URL('../../ui/icons/invite-player.svg', import.meta.url).href;
const LAST_TIME = new URL('../../ui/icons/last-time.svg', import.meta.url).href;
const LICHESS = new URL('../../ui/icons/lichess.svg', import.meta.url).href;
const LIKE = new URL('../../ui/icons/like.svg', import.meta.url).href;
const LIST = new URL('../../ui/icons/list.svg', import.meta.url).href;
const LOADING = new URL('../../ui/icons/loading.svg', import.meta.url).href;
const LOCATION = new URL('../../ui/icons/location.svg', import.meta.url).href;
const LOCATION_EMPTY = new URL('../../ui/icons/location-empty.svg', import.meta.url).href;
const LOCK = new URL('../../ui/icons/lock.svg', import.meta.url).href;
const LOGO = new URL('../../ui/images/logo-mark.svg', import.meta.url).href;
const MAP = new URL('../../ui/icons/map.svg', import.meta.url).href;
const MASTERCARD = new URL('../../ui/icons/mastercard.svg', import.meta.url).href;
const MASTERCARD_SQUARE = new URL('../../ui/icons/mastercard-square.svg', import.meta.url).href;
const MESSAGE = new URL('../../ui/icons/message.svg', import.meta.url).href;
const MESSAGE_EMPTY = new URL('../../ui/icons/message-empty.svg', import.meta.url).href;
const MINUS = new URL('../../ui/icons/minus.svg', import.meta.url).href;
const MUTUAL = new URL('../../ui/icons/mutual.svg', import.meta.url).href;
const MUTUAL_EMPTY = new URL('../../ui/icons/mutual-empty.svg', import.meta.url).href;
const NAVIGATE = new URL('../../ui/icons/navigate.svg', import.meta.url).href;
const NAVIGATE_EMPTY = new URL('../../ui/icons/navigate-empty.svg', import.meta.url).href;
const PROFILE = new URL('../../ui/icons/my-profile.svg', import.meta.url).href;
const PROFILE_EMPTY = new URL('../../ui/icons/my-profile-empty.svg', import.meta.url).href;
const PROFILE_STROKE = new URL('../../ui/icons/my-profile-stroke.svg', import.meta.url).href;
const PROFILE_INVERTED = new URL('../../ui/icons/my-profile-inverted.svg', import.meta.url).href;
const NOTIFICATIONS = new URL('../../ui/icons/notifications.svg', import.meta.url).href;
const NOTIFICATIONS_EMPTY = new URL('../../ui/icons/notifications-empty.svg', import.meta.url).href;
const OWNER = new URL('../../ui/icons/owner-star.svg', import.meta.url).href;
const OWNER_EMPTY = new URL('../../ui/icons/owner-star-empty.svg', import.meta.url).href;
const PAGE_MENU = new URL('../../ui/icons/page-menu.svg', import.meta.url).href;
const PAWN = new URL('../../ui/icons/pawn.svg', import.meta.url).href;
const PAWN_EMPTY = new URL('../../ui/icons/pawn-empty.svg', import.meta.url).href;
const PIN = new URL('../../ui/icons/pin.svg', import.meta.url).href;
const PIN_EMPTY = new URL('../../ui/icons/pin-empty.svg', import.meta.url).href;
const PLUS = new URL('../../ui/icons/plus.svg', import.meta.url).href;
const QR_CODE = new URL('../../ui/icons/qr-code.svg', import.meta.url).href;
const REFRESH = new URL('../../ui/icons/refresh.svg', import.meta.url).href;
const SWAP_COLOURS = new URL('../../ui/icons/swap-colours.svg', import.meta.url).href;
const STOP = new URL('../../ui/icons/stop.svg', import.meta.url).href;
const SHUFFLE = new URL('../../ui/icons/shuffle.svg', import.meta.url).href;
const RESULTS = new URL('../../ui/icons/results.svg', import.meta.url).href;
const RESULTS_EMPTY = new URL('../../ui/icons/results-empty.svg', import.meta.url).href;
const ROUTE = new URL('../../ui/icons/route.svg', import.meta.url).href;
const SMALL_ARROW_UP = new URL('../../ui/icons/s-arrow-up.svg', import.meta.url).href;
const SMALL_ARROW_LEFT = new URL('../../ui/icons/s-arrow-left.svg', import.meta.url).href;
const SMALL_ARROW_RIGHT = new URL('../../ui/icons/s-arrow-right.svg', import.meta.url).href;
const SMALL_ARROW_DOWN = new URL('../../ui/icons/s-arrow-down.svg', import.meta.url).href;
const SEARCH = new URL('../../ui/icons/search.svg', import.meta.url).href;
const SHARE = new URL('../../ui/icons/share.svg', import.meta.url).href;
const SPARK = new URL('../../ui/icons/spark.svg', import.meta.url).href;
const SPARK_CURSOR = new URL('../../ui/icons/spark-cursor.svg', import.meta.url).href;
const SPARK_CURSOR_EMPTY = new URL('../../ui/icons/spark-cursor-empty.svg', import.meta.url).href;
const SPARK_EMPTY = new URL('../../ui/icons/spark-empty.svg', import.meta.url).href;
const STAR = new URL('../../ui/icons/star.svg', import.meta.url).href;
const STAR_EMPTY = new URL('../../ui/icons/star-empty.svg', import.meta.url).href;
const SWORDS = new URL('../../ui/icons/swords.svg', import.meta.url).href;
const SETTINGS_GEAR = new URL('../../ui/icons/settings-gear.svg', import.meta.url).href;
const SETTINGS_GEAR_EMPTY = new URL('../../ui/icons/settings-gear-empty.svg', import.meta.url).href;
const STREET_LOCATION = new URL('../../ui/icons/street-location.svg', import.meta.url).href;
const TAP = new URL('../../ui/icons/tap.svg', import.meta.url).href;
const TARGET = new URL('../../ui/icons/target.svg', import.meta.url).href;
const TEXT_SIZE = new URL('../../ui/icons/text-size.svg', import.meta.url).href;
const THUMBS = new URL('../../ui/icons/thumbs.svg', import.meta.url).href;
const TIME = new URL('../../ui/icons/time.svg', import.meta.url).href;
const TOOLTIP = new URL('../../ui/icons/tooltip.svg', import.meta.url).href;
const TRASH = new URL('../../ui/icons/trash.svg', import.meta.url).href;
const TUNE = new URL('../../ui/icons/tune.svg', import.meta.url).href;
const VENUE = new URL('../../ui/icons/venue.svg', import.meta.url).href;
const WARNING = new URL('../../ui/icons/warning.svg', import.meta.url).href;
const WAZZING = new URL('../../ui/icons/wazzing.svg', import.meta.url).href;
const WALK_MARK = new URL('../../ui/icons/walk-mark.svg', import.meta.url).href;
const X_CLOSE = new URL('../../ui/icons/x-close.svg', import.meta.url).href;
const XS_ARROW_DOWN = new URL('../../ui/icons/xs-arrow-down.svg', import.meta.url).href;
const XS_ARROW_LEFT = new URL('../../ui/icons/xs-arrow-left.svg', import.meta.url).href;
const XS_ARROW_RIGHT = new URL('../../ui/icons/xs-arrow-right.svg', import.meta.url).href;
const XS_ARROW_UP = new URL('../../ui/icons/xs-arrow-up.svg', import.meta.url).href;
const AT = new URL('../../ui/icons/at.svg', import.meta.url).href;
const UPLOAD_FILE = new URL('../../ui/icons/upload-file.svg', import.meta.url).href;
const EVENT = new URL('../../ui/icons/pin-event.svg', import.meta.url).href;
const TOURNAMENT = new URL('../../ui/icons/pin-tournament.svg', import.meta.url).href;
const MY_LOCATION = new URL('../../ui/icons/pin-my-location.svg', import.meta.url).href;
const BADGE_MATCH = new URL('../../ui/icons/badge-match.svg', import.meta.url).href;
const KEY_UP = new URL('../../ui/icons/keyboard-up.svg', import.meta.url).href;
const KEY_DOWN = new URL('../../ui/icons/keyboard-down.svg', import.meta.url).href;
const SELECTED_PIN = new URL('../../ui/icons/selected-pin.svg', import.meta.url).href;

const gamIconUrls = {
  [GamIconName.UNKNOWN]: UNKNOWN,
  // Name
  [GamIconName.MAP_2D]: MAP_2D,
  [GamIconName.MAP_3D]: MAP_3D,
  [GamIconName.ADMIN]: ADMIN,
  [GamIconName.APPLE]: APPLE,
  [GamIconName.APPLEPAY]: APPLEPAY,
  [GamIconName.ARROW_DOWN]: ARROW_DOWN,
  [GamIconName.ARROW_LEFT]: ARROW_LEFT,
  [GamIconName.ARROW_RIGHT]: ARROW_RIGHT,
  [GamIconName.ARROW_RIGHT_END]: ARROW_RIGHT_END,
  [GamIconName.CHEVRON_LEFT]: CHEVRON_LEFT,
  [GamIconName.CHEVRON_RIGHT]: CHEVRON_RIGHT,
  [GamIconName.ACCCOUNT_SETTINGS]: ACCCOUNT_SETTINGS,
  [GamIconName.ARROW_UP]: ARROW_UP,
  [GamIconName.BELL_EMPTY]: BELL_EMPTY,
  [GamIconName.BELL]: BELL,
  [GamIconName.BIGMAC]: BIGMAC,
  [GamIconName.BIGMAC_EMPTY]: BIGMAC_EMPTY,
  [GamIconName.BIRD]: BIRD,
  [GamIconName.BULLET_LIST]: BULLET_LIST,
  [GamIconName.BULLET_LIST_EMPTY]: BULLET_LIST_EMPTY,
  [GamIconName.CAMERA]: CAMERA,
  [GamIconName.CHECK]: CHECK,
  [GamIconName.PAUSE]: PAUSE,
  [GamIconName.PLAY]: PLAY,
  [GamIconName.PLAY_EMPTY]: PLAY_EMPTY,
  [GamIconName.PLAY_OUTLINE]: PLAY_OUTLINE,
  [GamIconName.PUZZLE]: PUZZLE,
  [GamIconName.PUZZLE_GRID]: PUZZLE_GRID,
  [GamIconName.CIRCLES_GRID]: CIRCLES_GRID,
  [GamIconName.CHESS_CLOCK]: CHESS_CLOCK,
  [GamIconName.CHESS_COM]: CHESS_COM,
  [GamIconName.CHESS_SET]: CHESS_SET,
  [GamIconName.CHESS_TABLE]: CHESS_TABLE,
  [GamIconName.CLUB]: CLUB,
  [GamIconName.COFFEE]: COFFEE,
  [GamIconName.COMPASS]: COMPASS,
  [GamIconName.CORNER]: CORNER,
  [GamIconName.CROWN]: CROWN,
  [GamIconName.DOTS_H]: DOTS_H,
  [GamIconName.DOTS_H_EMPTY]: DOTS_H_EMPTY,
  [GamIconName.DOTS_V]: DOTS_V,
  [GamIconName.DOUBLE_CHECK]: DOUBLE_CHECK,
  [GamIconName.DOWN]: DOWN,
  [GamIconName.DOUBLE_DOWN]: DOUBLE_DOWN,
  [GamIconName.EDIT]: EDIT,
  [GamIconName.ENVELOPE]: ENVELOPE,
  [GamIconName.EXPLORE]: EXPLORE,
  [GamIconName.EXPLORE_EMPTY]: EXPLORE_EMPTY,
  [GamIconName.FAST]: FAST,
  [GamIconName.FAST_EMPTY]: FAST_EMPTY,
  [GamIconName.FAST_PAUSE]: FAST_PAUSE,
  [GamIconName.FEED]: FEED,
  [GamIconName.FEMALE]: FEMALE,
  [GamIconName.FIDE]: FIDE,
  [GamIconName.FILTER]: FILTER,
  [GamIconName.FILTER_LEGIBLE]: FILTER_LEGIBLE,
  [GamIconName.FILTER_APPLIED]: FILTER_APPLIED,
  [GamIconName.TILDE]: TILDE,
  [GamIconName.GAMBIT]: GAMBIT,
  [GamIconName.GAMBIT_EMPTY]: GAMBIT_EMPTY,
  [GamIconName.GAMBIT_HALF]: GAMBIT_HALF,
  [GamIconName.GIANT_CHESS]: GIANT_CHESS,
  [GamIconName.GOOGLE]: GOOGLE,
  [GamIconName.HAMBURGER]: HAMBURGER,
  [GamIconName.HANDSHAKE]: HANDSHAKE,
  [GamIconName.HANDSHAKE_EMPTY]: HANDSHAKE_EMPTY,
  [GamIconName.HANDSHAKE_HALF_LEFT]: HANDSHAKE_HALF_LEFT,
  [GamIconName.HANDSHAKE_HALF_RIGHT]: HANDSHAKE_HALF_RIGHT,
  [GamIconName.HEART]: HEART,
  [GamIconName.HEART_EMPTY]: HEART_EMPTY,
  [GamIconName.HOUSE]: HOUSE,
  [GamIconName.IMAGE]: IMAGE,
  [GamIconName.INSTAGRAM]: INSTAGRAM,
  [GamIconName.INVITE]: INVITE,
  [GamIconName.INVITE_EMPTY]: INVITE_EMPTY,
  [GamIconName.INVITE_PLAYER]: INVITE_PLAYER,
  [GamIconName.LAST_TIME]: LAST_TIME,
  [GamIconName.LICHESS]: LICHESS,
  [GamIconName.LIKE]: LIKE,
  [GamIconName.LIST]: LIST,
  [GamIconName.LOADING]: LOADING,
  [GamIconName.LOCATION]: LOCATION,
  [GamIconName.LOCATION_EMPTY]: LOCATION_EMPTY,
  [GamIconName.LOCK]: LOCK,
  [GamIconName.LOGO]: LOGO,
  [GamIconName.MAP]: MAP,
  [GamIconName.MASTERCARD]: MASTERCARD,
  [GamIconName.MASTERCARD_SQUARE]: MASTERCARD_SQUARE,
  [GamIconName.MESSAGE]: MESSAGE,
  [GamIconName.MESSAGE_EMPTY]: MESSAGE_EMPTY,
  [GamIconName.MINUS]: MINUS,
  [GamIconName.MUTUAL]: MUTUAL,
  [GamIconName.MUTUAL_EMPTY]: MUTUAL_EMPTY,
  [GamIconName.NAVIGATE]: NAVIGATE,
  [GamIconName.NAVIGATE_EMPTY]: NAVIGATE_EMPTY,
  [GamIconName.PROFILE]: PROFILE,
  [GamIconName.PROFILE_EMPTY]: PROFILE_EMPTY,
  [GamIconName.PROFILE_STROKE]: PROFILE_STROKE,
  [GamIconName.PROFILE_INVERTED]: PROFILE_INVERTED,
  [GamIconName.NOTIFICATIONS]: NOTIFICATIONS,
  [GamIconName.NOTIFICATIONS_EMPTY]: NOTIFICATIONS_EMPTY,
  [GamIconName.OWNER]: OWNER,
  [GamIconName.OWNER_EMPTY]: OWNER_EMPTY,
  [GamIconName.PAGE_MENU]: PAGE_MENU,
  [GamIconName.PAWN]: PAWN,
  [GamIconName.PAWN_EMPTY]: PAWN_EMPTY,
  [GamIconName.PIN]: PIN,
  [GamIconName.PIN_EMPTY]: PIN_EMPTY,
  [GamIconName.PLUS]: PLUS,
  [GamIconName.QR_CODE]: QR_CODE,
  [GamIconName.REFRESH]: REFRESH,
  [GamIconName.SWAP_COLOURS]: SWAP_COLOURS,
  [GamIconName.STOP]: STOP,
  [GamIconName.SHUFFLE]: SHUFFLE,
  [GamIconName.RESULTS]: RESULTS,
  [GamIconName.RESULTS_EMPTY]: RESULTS_EMPTY,
  [GamIconName.ROUTE]: ROUTE,
  [GamIconName.SMALL_ARROW_UP]: SMALL_ARROW_UP,
  [GamIconName.SMALL_ARROW_LEFT]: SMALL_ARROW_LEFT,
  [GamIconName.SMALL_ARROW_RIGHT]: SMALL_ARROW_RIGHT,
  [GamIconName.SMALL_ARROW_DOWN]: SMALL_ARROW_DOWN,
  [GamIconName.SEARCH]: SEARCH,
  [GamIconName.SHARE]: SHARE,
  [GamIconName.SPARK]: SPARK,
  [GamIconName.SPARK_CURSOR]: SPARK_CURSOR,
  [GamIconName.SPARK_CURSOR_EMPTY]: SPARK_CURSOR_EMPTY,
  [GamIconName.SPARK_EMPTY]: SPARK_EMPTY,
  [GamIconName.STAR]: STAR,
  [GamIconName.STAR_EMPTY]: STAR_EMPTY,
  [GamIconName.SWORDS]: SWORDS,
  [GamIconName.SETTINGS_GEAR]: SETTINGS_GEAR,
  [GamIconName.SETTINGS_GEAR_EMPTY]: SETTINGS_GEAR_EMPTY,
  [GamIconName.STREET_LOCATION]: STREET_LOCATION,
  [GamIconName.TAP]: TAP,
  [GamIconName.TARGET]: TARGET,
  [GamIconName.TEXT_SIZE]: TEXT_SIZE,
  [GamIconName.THUMBS]: THUMBS,
  [GamIconName.TIME]: TIME,
  [GamIconName.TOOLTIP]: TOOLTIP,
  [GamIconName.TRASH]: TRASH,
  [GamIconName.TUNE]: TUNE,
  [GamIconName.VENUE]: VENUE,
  [GamIconName.WARNING]: WARNING,
  [GamIconName.WALK]: WAZZING,
  [GamIconName.WALK_MARK]: WALK_MARK,
  [GamIconName.X_CLOSE]: X_CLOSE,
  [GamIconName.XS_ARROW_DOWN]: XS_ARROW_DOWN,
  [GamIconName.XS_ARROW_LEFT]: XS_ARROW_LEFT,
  [GamIconName.XS_ARROW_RIGHT]: XS_ARROW_RIGHT,
  [GamIconName.XS_ARROW_UP]: XS_ARROW_UP,
  [GamIconName.AT]: AT,
  [GamIconName.UPLOAD_FILE]: UPLOAD_FILE,
  // SpecialName
  [GamIconSpecialName.EVENT]: EVENT,
  [GamIconSpecialName.TOURNAMENT]: TOURNAMENT,
  [GamIconSpecialName.MY_LOCATION]: MY_LOCATION,
  [GamIconSpecialName.BADGE_MATCH]: BADGE_MATCH,
  [GamIconSpecialName.KEY_UP]: KEY_UP,
  [GamIconSpecialName.KEY_DOWN]: KEY_DOWN,
  [GamIconSpecialName.SELECTED_PIN]: SELECTED_PIN,
};
</script>

<style lang="scss">
.svg-container {
  display: flex;

  &.circle {
    border-radius: 100px;
  }

  &.filled {
    background: var(--color-pink);
  }

  &.padded {
    padding: 0.5rem;
  }

  &.flag {
    border-radius: 4px;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    position: relative;
  }

  .svg-class {
    align-items: center;
    justify-content: center;
    max-height: 100%;
    width: 100%;
    height: 100%;
  }
}
</style>
